import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { EmailPreview } from "./EmailPreview";

interface EmailChip {
  email: string;
  id: string;
}

export const SuccessModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [emails, setEmails] = useState<EmailChip[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.endsWith(",")) {
      const email = value.slice(0, -1).trim();
      if (email && isValidEmail(email)) {
        setEmails([...emails, { email, id: crypto.randomUUID() }]);
        setInputValue("");
      }
    } else {
      setInputValue(value);
    }
  };

  const handleSubmit = () => {
    // Handle submission of friend emails here
    console.log("Sending to friends:", emails);
  };

  const removeEmail = (id: string) => {
    setEmails(emails.filter((email) => email.id !== id));
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Newsletter Setup Complete! 🎉</DialogTitle>
          <DialogDescription>
            Check your inbox for the test email. While you wait, why not invite
            your friends to your newsletter?
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Add a Personal Message (Optional)
              </label>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value)}
                className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                placeholder="Add a personal note to your invitation..."
                maxLength={300}
              />
              <p className="text-xs text-gray-500 text-right">
                {personalMessage.length}/300 characters
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Add friend's emails (separate with commas)
              </label>
              <Input
                type="email"
                placeholder="friend@example.com"
                value={inputValue}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>

            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {emails.map((email) => (
                  <div
                    key={email.id}
                    className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full"
                  >
                    <span className="text-sm">{email.email}</span>
                    <button
                      onClick={() => removeEmail(email.id)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Button
              onClick={handleSubmit}
              className="w-full"
              disabled={emails.length === 0}
            >
              Share Newsletter
            </Button>
          </div>

          <EmailPreview personalMessage={personalMessage} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
